<template>
    <div class="builder">
        <div class="builder__input">
            <div class="form-group">
                <div class="input-group">
                    <input
                        v-model="url"
                        type="text"
                        class="form-control"
                        @input="parseUrl"
                    >
                    <span class="input-group-btn">
                        <button
                            class="btn btn-default"
                            type="button"
                            @click.prevent="openUrl"
                        >
                            Open
                        </button>
                    </span>
                </div>
            </div>
        </div>
        <div class="builder__configurator">
            <div class="row">
                <div class="col-sm-4">
                    <div class="form-group">
                        <input
                            v-model="account"
                            type="text"
                            class="form-control"
                            placeholder="Account name"
                        >
                    </div>
                </div>
                <div class="col-sm-4">
                    <div class="form-group">
                        <input
                            v-model="format"
                            type="text"
                            class="form-control"
                            placeholder="Format"
                        >
                    </div>
                </div>
                <div class="col-sm-4">
                    <div class="form-group">
                        <input
                            v-model="quality"
                            type="text"
                            pattern="[0-9]*"
                            class="form-control"
                            placeholder="Quality"
                        >
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-sm-3">
                    <div class="form-group">
                        <input
                            v-model.number="width"
                            type="text"
                            pattern="[0-9]*"
                            class="form-control"
                            placeholder="Width"
                        >
                    </div>
                </div>
                <div class="col-sm-3">
                    <div class="form-group">
                        <input
                            v-model.number="height"
                            type="text"
                            pattern="[0-9]*"
                            class="form-control"
                            placeholder="Height"
                        >
                    </div>
                </div>
                <div class="col-sm-3">
                    <div class="form-group">
                        <select
                            v-model="fit"
                            class="form-control"
                            placeholder="Fit..."
                        >
                            <option
                                disabled
                                value=""
                            >
                                Fit...
                            </option>
                            <option value="fit">
                                Fit
                            </option>
                            <option value="crop">
                                Crop
                            </option>
                        </select>
                    </div>
                </div>
                <div class="col-sm-3">
                    <div class="form-group">
                        <input
                            v-model="background"
                            type="text"
                            class="form-control"
                            placeholder="Background"
                        >
                    </div>
                </div>
            </div>

            <div class="form-group">
                <div class="box-actions">
                    <button
                        class="btn btn-sm btn-primary"
                        @click.prevent="addLayer"
                    >
                        Add a layer
                    </button>
                </div>
            </div>

            <div
                v-for="(layer, index) in layers"
                :key="layer.id"
                class="layer"
            >
                <div class="box-actions with-border">
                    <span class="action-simple-value">
                        Layer {{ index + 1 }}

                        <button
                            class="btn btn-xs btn-danger pull-right"
                            @click.prevent="removeLayer(layer)"
                        >
                            Remove
                        </button>
                    </span>
                </div>

                <div class="row mt-3">
                    <div class="col-sm-4">
                        <div class="form-group">
                            <input
                                v-model="layer.src"
                                type="text"
                                class="form-control"
                                placeholder="Source image"
                            >
                        </div>
                    </div>
                    <div class="col-sm-4">
                        <div class="form-group">
                            <input
                                v-model="layer.vmask"
                                type="text"
                                class="form-control"
                                placeholder="View mask"
                            >
                        </div>
                    </div>
                    <div class="col-sm-4">
                        <div class="form-group">
                            <input
                                v-model="layer.omask"
                                type="text"
                                class="form-control"
                                placeholder="Object mask"
                            >
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-sm-3">
                        <div class="form-group">
                            <input
                                v-model="layer.blend"
                                type="text"
                                class="form-control"
                                placeholder="Blend"
                            >
                        </div>
                    </div>
                </div>

                <p class="text-muted">
                    2D transforms
                </p>

                <div class="row">
                    <div class="col-sm-3">
                        <div class="form-group">
                            <input
                                v-model="layer.pos"
                                type="text"
                                pattern="(-?[0-9.]+,-?[0-9.]+)|(-?[0-9.]+,-?[0-9.]+,-?[0-9.]+)"
                                class="form-control"
                                placeholder="Offset (x,y)"
                            >
                        </div>
                    </div>
                    <div class="col-sm-3">
                        <div class="form-group">
                            <input
                                v-model="layer.size"
                                pattern="-?[0-9.]+,-?[0-9.]+"
                                type="text"
                                class="form-control"
                                placeholder="Size (w,h)"
                            >
                        </div>
                    </div>
                    <div class="col-sm-3">
                        <div class="form-group">
                            <input
                                v-model="layer.scale"
                                type="text"
                                pattern="-?[0-9.]+(,-?[0-9.]+)?"
                                class="form-control"
                                placeholder="Scale (s, or sx,sy)"
                            >
                        </div>
                    </div>
                    <div class="col-sm-3">
                        <div class="form-group">
                            <input
                                v-model="layer.rotate"
                                type="text"
                                pattern="-?[0-9.]+"
                                class="form-control"
                                placeholder="Rotate (deg)"
                            >
                        </div>
                    </div>
                </div>

                <p class="text-muted">
                    Object configuration
                </p>

                <div class="row">
                    <div class="col-sm-3">
                        <div class="form-group">
                            <input
                                v-model="layer.opos"
                                type="text"
                                pattern="(-?[0-9.]+,-?[0-9.]+)|(-?[0-9.]+,-?[0-9.]+,-?[0-9.]+)"
                                class="form-control"
                                placeholder="Object Offset"
                            >
                        </div>
                    </div>
                    <div class="col-sm-3">
                        <div class="form-group">
                            <input
                                v-model="layer.osize"
                                pattern="-?[0-9.]+,-?[0-9.]+"
                                type="text"
                                class="form-control"
                                placeholder="Object Size"
                            >
                        </div>
                    </div>
                    <div class="col-sm-3">
                        <div class="form-group">
                            <input
                                v-model="layer.oscale"
                                type="text"
                                pattern="-?[0-9.]+(,-?[0-9.]+)?"
                                class="form-control"
                                placeholder="Object Scale"
                            >
                        </div>
                    </div>
                    <div class="col-sm-3">
                        <div class="form-group">
                            <input
                                v-model="layer.orotate"
                                type="text"
                                pattern="-?[0-9.]+"
                                class="form-control"
                                placeholder="Object Rotate"
                            >
                        </div>
                    </div>
                </div>

                <p class="text-muted">
                    3D transforms
                </p>

                <div class="row">
                    <div class="col-sm-12">
                        <div class="form-group">
                            <label>
                                <input
                                    v-model="layer.match3d"
                                    type="checkbox"
                                > Interactive/perspective matching
                            </label>
                        </div>

                        <div
                            v-if="layer.match3d"
                            class="form-group"
                        >
                            <input
                                v-model="layer.matrix3d"
                                class="form-control"
                                type="text"
                                @input="matrixUpdated(layer)"
                            >
                        </div>

                        <template v-if="layer.match3d">
                            <div
                                v-for="(pt, ptIndex) in layer.box"
                                :key="ptIndex"
                                class="row mb-2"
                            >
                                <div class="col-sm-3">
                                    <input
                                        v-model.number="pt[0]"
                                        class="form-control"
                                        @input="updateBoxes(layer)"
                                    >
                                </div>

                                <div class="col-sm-3">
                                    <input
                                        v-model.number="pt[1]"
                                        class="form-control"
                                        @input="updateBoxes(layer)"
                                    >
                                </div>

                                <div
                                    v-if="ptIndex === 0"
                                    class="col-sm-6"
                                >
                                    <div class="pull-right">
                                        <div class="form-group">
                                            <button
                                                class="bn btn-sm btn-default"
                                                @click.prevent="roundBoxes(layer)"
                                            >
                                                Round
                                            </button>
                                        </div>

                                        <div class="form-group">
                                            <button
                                                class="bn btn-sm btn-default"
                                                @click.prevent="rotate180(layer)"
                                            >
                                                Rotate 180°
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>

                <div
                    v-if="!layer.match3d"
                    class="row"
                >
                    <div
                        v-for="t in layer.transforms"
                        :key="t.id"
                        class="col-sm-6 mb-3"
                    >
                        <div class="input-group">
                            <span class="input-group-btn">
                                <button
                                    :class="['btn', 'btn-default', { 'active': t.type === 'T' }]"
                                    type="button"
                                    @click.prevent="setTransform(t, 'T')"
                                >
                                    T
                                </button>
                                <button
                                    :class="['btn', 'btn-default', { 'active': t.type === 'R' }]"
                                    type="button"
                                    @click.prevent="setTransform(t, 'R')"
                                >
                                    R
                                </button>
                                <button
                                    :class="['btn', 'btn-default', { 'active': t.type === 'S' }]"
                                    type="button"
                                    @click.prevent="setTransform(t, 'S')"
                                >
                                    S
                                </button>
                            </span>
                            <input
                                v-model="t.value"
                                type="text"
                                pattern="-?[0-9.]+(,-?[0-9.]+)*"
                                class="form-control"
                                placeholder="Transform..."
                                @input="addTransform(layer)"
                            >
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div
            :class="[
                'builder__preview',
                { 'builder__preview--no-max': noMaxWidth },
            ]"
        >
            <div
                v-if="url && url.length > 0"
                class="builder__preview-sticky"
            >
                <div class="preview__image-container">
                    <div class="preview__image-square">
                        <div
                            class="preview__image-zoom"
                            :style="{
                                transform: `translate(${scaleX}px, ${scaleY}px) scale(${scale})`
                            }"
                        >
                            <div
                                ref="image"
                                class="preview__image"
                                :style="{
                                    backgroundImage: 'url(' + url + ')',
                                }"
                            />

                            <template v-if="showControls">
                                <template
                                    v-for="(layer, layerIndex) in layers"
                                    class="layer"
                                >
                                    <template v-if="layer.match3d">
                                        <svg
                                            ref="background"
                                            :key="layer.id"
                                            class="control-box__background"
                                            :style="layerStyle(layer)"
                                            viewBox="0 0 100 100"
                                            preserveAspectRatio="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            :data-layer="layerIndex"
                                        >
                                            <polygon
                                                fill="rgb(0, 0, 0)"
                                                opacity="0.2"
                                                :points="layerBox(layer)"
                                            />
                                        </svg>
                                        <div
                                            v-for="(box, index) in layer.box"
                                            :key="index"
                                            ref="corner"
                                            class="control-box__point"
                                            :style="boxPoint(box, layer)"
                                            :data-layer="layerIndex"
                                            :data-index="index"
                                        />
                                    </template>
                                </template>
                            </template>
                        </div>
                    </div>

                    <div class="preview__controls">
                        <div class="form-group">
                            <label>
                                <input
                                    v-model="showControls"
                                    type="checkbox"
                                > Show mapping controls
                            </label>
                        </div>

                        <div class="form-group">
                            <label>
                                <input
                                    v-model="noMaxWidth"
                                    type="checkbox"
                                > Remove image width limit
                            </label>
                        </div>

                        <div class="form-group">
                            <div class="help-block">
                                Hold Option to move vertical walls at corners, or resize all corners by dragging at the center
                            </div>
                        </div>

                        <div class="form-group">
                            <div class="help-block">
                                Hold Shift to move horizontal walls
                            </div>
                        </div>

                        <div class="form-group">
                            <div class="help-block">
                                Hold Shift+Option to move corners
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-sm-3">
                                Scale
                            </div>
                            <div class="col-sm-8">
                                <div class="form-group">
                                    <div class="input-group">
                                        <span class="input-group-btn">
                                            <button
                                                class="btn btn-default"
                                                type="button"
                                                @click.prevent="updateScale(-0.25, 0, 0)"
                                            >
                                                -
                                            </button>
                                        </span>
                                        <span class="input-group-btn">
                                            <button
                                                class="btn btn-default"
                                                type="button"
                                                @click.prevent="updateScale(0.25, 0, 0)"
                                            >
                                                +
                                            </button>
                                        </span>
                                        <input
                                            v-model="scale"
                                            type="text"
                                            class="form-control"
                                        >
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-sm-3">
                                Offset X
                            </div>
                            <div class="col-sm-8">
                                <div class="form-group">
                                    <div class="input-group">
                                        <span class="input-group-btn">
                                            <button
                                                class="btn btn-default"
                                                type="button"
                                                @click.prevent="updateScale(0, -25, 0)"
                                            >
                                                -
                                            </button>
                                        </span>
                                        <span class="input-group-btn">
                                            <button
                                                class="btn btn-default"
                                                type="button"
                                                @click.prevent="updateScale(0, 25, 0)"
                                            >
                                                +
                                            </button>
                                        </span>
                                        <input
                                            v-model="scaleX"
                                            type="text"
                                            class="form-control"
                                        >
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-sm-3">
                                Offset Y
                            </div>

                            <div class="col-sm-8">
                                <div class="form-group">
                                    <div class="input-group">
                                        <span class="input-group-btn">
                                            <button
                                                class="btn btn-default"
                                                type="button"
                                                @click.prevent="updateScale(0, 0, -25)"
                                            >
                                                -
                                            </button>
                                        </span>
                                        <span class="input-group-btn">
                                            <button
                                                class="btn btn-default"
                                                type="button"
                                                @click.prevent="updateScale(0, 0, 25)"
                                            >
                                                +
                                            </button>
                                        </span>
                                        <input
                                            v-model="scaleY"
                                            type="text"
                                            class="form-control"
                                        >
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Vue from 'vue';
    import interact from 'interactjs';
    import numeric from 'numeric';
    import parse from 'url-parse';

    export default {
        data() {
            return {
                account: '',
                format: 'png-alpha',
                quality: 85,
                width: null,
                height: null,
                background: null,
                layers: [],
                fit: '',
                url: '',
                showControls: true,
                noMaxWidth: false,
                scale: 1,
                scaleX: 0,
                scaleY: 0,
                unique: 0,
            };
        },
        computed: {
            w() {
                return this.width != null ? this.width : 1000;
            },

            h() {
                return this.height != null ? this.height : 1000;
            },

            ws() {
                if (this.w > this.h) {
                    return 1;
                }

                return this.h / this.w;
            },

            hs() {
                if (this.h > this.w) {
                    return 1;
                }

                return this.w / this.h;
            },
        },
        watch: {
            layers: {
                handler: function layers() {
                    this.refresh();
                    this.updateBox();

                    this.layers.forEach((l) => {
                        if (l.tempMatrix) {
                            l.matrix3d = l.tempMatrix;
                            delete l.tempMatrix;
                            this.reverseTransform(l);
                        }
                    });
                },
                deep: true,
            },

            showControls() {
                this.enableInteract();
            },

            noMaxWidth() {
                this.enableInteract();
            },

            width() {
                this.refresh();
            },

            height() {
                this.refresh();
            },

            account() {
                this.refresh();
            },

            format() {
                this.refresh();
            },

            quality() {
                this.refresh();
            },

            background() {
                this.refresh();
            },

            fit() {
                this.refresh();
            },
        },
        methods: {
            newLayer() {
                this.unique += 1;
                return {
                    id: `${new Date().getTime()}-${this.unique}`,
                    src: '',

                    vmask: '',
                    omask: '',

                    blend: '',

                    pattern: '',
                    patternScale: null,

                    size: '',
                    scale: '',
                    rotate: '',
                    pos: '',

                    osize: '',
                    oscale: '',
                    orotate: '',
                    opos: '',

                    matrix3d: '',

                    match3d: false,
                    box: null,

                    oldMatch3d: false,
                    oldOSize: null,

                    transforms: [{
                        type: 'T',
                        value: '',
                    }],
                };
            },

            addLayer() {
                this.layers.push(this.newLayer());
            },

            removeLayer(layer) {
                this.layers = this.layers.filter(l => l !== layer);
            },

            addTransform(layer) {
                if (this.showMoreTransforms(layer)) {
                    layer.transforms.push({
                        id: new Date().getTime(),
                        type: 'T',
                        value: '',
                    });
                }
            },

            showMoreTransforms(layer) {
                if (layer.transforms[layer.transforms.length - 1].value === '') {
                    return false;
                }

                return true;
            },

            setTransform(t, type) {
                t.type = type;
            },

            refresh() {
                if (this.updateTimer) {
                    clearTimeout(this.updateTimer);
                }

                this.updateTimer = setTimeout(() => {
                    this.reload();
                }, 250);
            },

            reload() {
                if (!this.account) {
                    return;
                }

                let url = `/api/v1/image/${this.account}/?`;

                this.layers.forEach((layer, index) => {
                    if (url.substr(-1) !== '/') {
                        url += '&';
                    }

                    url += `layer=${index + 1}`;

                    if (layer.src && layer.src.length > 0) {
                        url += `&src=${layer.src}`;
                    }

                    if (layer.vmask && layer.vmask.length > 0) {
                        url += `&mask=${layer.vmask}`;
                    }

                    if (layer.omask && layer.omask.length > 0) {
                        url += `&omask=${layer.omask}`;
                    }

                    if (layer.size && layer.size.length > 0) {
                        url += `&size=${layer.size}`;
                    }

                    if (layer.scale && layer.scale.length > 0) {
                        url += `&scale=${layer.scale}`;
                    }

                    if (layer.rotate && layer.rotate.length > 0) {
                        url += `&rotate=${layer.rotate}`;
                    }

                    if (layer.pos && layer.pos.length > 0) {
                        url += `&pos=${layer.pos}`;
                    }

                    if (layer.osize && layer.osize.length > 0) {
                        url += `&osize=${layer.osize}`;
                    }

                    if (layer.oscale && layer.oscale.length > 0) {
                        url += `&oscale=${layer.oscale}`;
                    }

                    if (layer.orotate && layer.orotate.length > 0) {
                        url += `&orotate=${layer.orotate}`;
                    }

                    if (layer.opos && layer.opos.length > 0) {
                        url += `&opos=${layer.opos}`;
                    }

                    if (layer.blend && layer.blend.length > 0) {
                        url += `&blend=${layer.blend}`;
                    }

                    if (layer.match3d) {
                        url += `&matrix3d=${layer.matrix3d}`;
                    } else {
                        layer.transforms.forEach((t) => {
                            if (t.value.length > 0) {
                                if (t.type === 'R') {
                                    url += `&rotate3d=${t.value}`;
                                }
                                if (t.type === 'S') {
                                    url += `&scale3d=${t.value}`;
                                }
                                if (t.type === 'T') {
                                    url += `&pos3d=${t.value}`;
                                }
                            }
                        });
                    }
                });

                if (this.width && this.width.length > 0) {
                    url += `&wid=${this.width}`;
                }

                if (this.height && this.height.length > 0) {
                    url += `&hei=${this.height}`;
                }

                if (this.fit && this.fit.length > 0) {
                    url += `&fit=${this.fit}`;
                }

                if (this.background && this.background.length > 0) {
                    url += `&bgc=${this.background}`;
                }

                if (this.format) {
                    url += `&fmt=${this.format}`;
                }

                if (this.quality && this.quality.length > 0) {
                    url += `&qlt=${this.quality}`;
                }

                this.url = url;
            },

            updateBox() {
                this.layers.forEach((layer) => {
                    if (layer.match3d !== layer.oldMatch3d && layer.match3d) {
                        this.updateLayerBox(layer);
                    }

                    if (layer.match3d && layer.osize !== layer.oldOSize) {
                        this.updateLayerBox(layer);
                    }
                });
            },

            updateLayerBox(layer) {
                layer.oldMatch3d = layer.match3d;
                layer.oldOSize = layer.osize;

                const size = layer.osize.split(',').map(x => parseInt(x.trim(), 10) * 0.5);

                layer.box = [
                    [-size[0], -size[1], 0, 0],
                    [size[0], -size[1], 0, 0],
                    [size[0], size[1], 0, 0],
                    [-size[0], size[1], 0, 0],
                ];

                layer.boxMapFrom = [
                    [-size[0], -size[1]],
                    [size[0], -size[1]],
                    [size[0], size[1]],
                    [-size[0], size[1]],
                ];

                this.enableInteract();
                this.updateTransform(layer);
            },

            updateBoxes(layer) {
                this.$nextTick(() => {
                    this.updateTransform(layer);
                });
            },

            layerBox(layer) {
                const minX = Math.min(
                    layer.box[0][0] + layer.box[0][2],
                    layer.box[1][0] + layer.box[1][2],
                    layer.box[2][0] + layer.box[2][2],
                    layer.box[3][0] + layer.box[3][2]
                );
                const minY = Math.min(
                    layer.box[0][1] + layer.box[0][3],
                    layer.box[1][1] + layer.box[1][3],
                    layer.box[2][1] + layer.box[2][3],
                    layer.box[3][1] + layer.box[3][3]
                );

                const w = this.layerWidth(layer);
                const h = this.layerHeight(layer);

                return layer.box.map((p) => {
                    const x = (p[0] + p[2] - minX) / w * 100;
                    const y = (p[1] + p[3] - minY) / h * 100;

                    return `${x.toFixed(2)},${y.toFixed(2)}`;
                }).join(' ');
            },

            layerDimensions(layer) {
                const minX = Math.min(
                    layer.box[0][0] + layer.box[0][2],
                    layer.box[1][0] + layer.box[1][2],
                    layer.box[2][0] + layer.box[2][2],
                    layer.box[3][0] + layer.box[3][2]
                );
                const maxX = Math.max(
                    layer.box[0][0] + layer.box[0][2],
                    layer.box[1][0] + layer.box[1][2],
                    layer.box[2][0] + layer.box[2][2],
                    layer.box[3][0] + layer.box[3][2]
                );
                const minY = Math.min(
                    layer.box[0][1] + layer.box[0][3],
                    layer.box[1][1] + layer.box[1][3],
                    layer.box[2][1] + layer.box[2][3],
                    layer.box[3][1] + layer.box[3][3]
                );
                const maxY = Math.max(
                    layer.box[0][1] + layer.box[0][3],
                    layer.box[1][1] + layer.box[1][3],
                    layer.box[2][1] + layer.box[2][3],
                    layer.box[3][1] + layer.box[3][3]
                );

                return {
                    minX,
                    maxX,
                    minY,
                    maxY,
                };
            },

            layerWidth(layer) {
                const { minX, maxX } = this.layerDimensions(layer);

                return maxX - minX;
            },

            layerHeight(layer) {
                const { minY, maxY } = this.layerDimensions(layer);

                return maxY - minY;
            },

            layerStyle(layer) {
                const { minX, minY } = this.layerDimensions(layer);

                return {
                    left: `${((minX) / this.w / this.ws * 100 + 50).toFixed(2)}%`,
                    top: `${((minY) / this.h / this.hs * 100 + 50).toFixed(2)}%`,
                    width: `${(this.layerWidth(layer) / this.w / this.ws * 100).toFixed(2)}%`,
                    height: `${(this.layerHeight(layer) / this.h / this.hs * 100).toFixed(2)}%`,
                };
            },

            boxPoint(box) {
                return {
                    left: `${((box[0] + (box[2] || 0)) / this.w / this.ws * 100 + 50).toFixed(2)}%`,
                    top: `${((box[1] + (box[3] || 0)) / this.h / this.hs * 100 + 50).toFixed(2)}%`,
                };
            },

            enableInteract() {
                if (this.interractables) {
                    this.interractables.forEach((i) => {
                        interact(i).unset();
                    });

                    this.interractables = null;
                }

                this.$nextTick(() => {
                    this.$nextTick(() => {
                        this.interractables = [];

                        if (this.$refs.corner) {
                            this.$refs.corner.forEach((c) => {
                                this.interractables.push(c);

                                interact(c).draggable({
                                    listeners: {
                                        end: (event) => {
                                            const layerIndex = event.target.dataset.layer;
                                            const pointIndex = event.target.dataset.index;

                                            const layer = this.layers[layerIndex];
                                            if (layer) {
                                                const point = layer.box[pointIndex];
                                                if (point) {
                                                    const dx = (event.pageX - event.x0) / this.scale;
                                                    const dy = (event.pageY - event.y0) / this.scale;

                                                    const width = this.$refs.image.offsetWidth;
                                                    const height = this.$refs.image.offsetHeight;

                                                    Vue.set(point, 0, point[0] + (dx / width * this.w * this.ws));
                                                    Vue.set(point, 1, point[1] + (dy / height * this.h * this.hs));
                                                    Vue.set(point, 2, 0);
                                                    Vue.set(point, 3, 0);

                                                    if (event.shiftKey) {
                                                        if (+pointIndex === 0) {
                                                            Vue.set(layer.box[1], 1, layer.box[0][1]);
                                                            Vue.set(layer.box[1], 3, layer.box[0][3]);
                                                        }
                                                        if (+pointIndex === 1) {
                                                            Vue.set(layer.box[0], 1, layer.box[1][1]);
                                                            Vue.set(layer.box[0], 3, layer.box[1][3]);
                                                        }
                                                        if (+pointIndex === 2) {
                                                            Vue.set(layer.box[3], 1, layer.box[2][1]);
                                                            Vue.set(layer.box[3], 3, layer.box[2][3]);
                                                        }
                                                        if (+pointIndex === 3) {
                                                            Vue.set(layer.box[2], 1, layer.box[3][1]);
                                                            Vue.set(layer.box[2], 3, layer.box[3][3]);
                                                        }
                                                    }

                                                    if (event.altKey) {
                                                        if (+pointIndex === 0) {
                                                            Vue.set(layer.box[3], 0, layer.box[0][0]);
                                                            Vue.set(layer.box[3], 2, layer.box[0][2]);
                                                        }
                                                        if (+pointIndex === 1) {
                                                            Vue.set(layer.box[2], 0, layer.box[1][0]);
                                                            Vue.set(layer.box[2], 2, layer.box[1][2]);
                                                        }
                                                        if (+pointIndex === 2) {
                                                            Vue.set(layer.box[1], 0, layer.box[2][0]);
                                                            Vue.set(layer.box[1], 2, layer.box[2][2]);
                                                        }
                                                        if (+pointIndex === 3) {
                                                            Vue.set(layer.box[0], 0, layer.box[3][0]);
                                                            Vue.set(layer.box[0], 2, layer.box[3][2]);
                                                        }
                                                    }

                                                    this.updateTransform(layer);
                                                }
                                            }
                                        },

                                        move: (event) => {
                                            const layerIndex = event.target.dataset.layer;
                                            const pointIndex = event.target.dataset.index;

                                            const layer = this.layers[layerIndex];
                                            if (layer) {
                                                const point = layer.box[pointIndex];
                                                if (point) {
                                                    const dx = (event.pageX - event.x0) / this.scale;
                                                    const dy = (event.pageY - event.y0) / this.scale;

                                                    const width = this.$refs.image.offsetWidth;
                                                    const height = this.$refs.image.offsetHeight;

                                                    Vue.set(point, 2, dx / width * this.w * this.ws);
                                                    Vue.set(point, 3, dy / height * this.h * this.hs);

                                                    if (event.shiftKey) {
                                                        if (+pointIndex === 0) {
                                                            Vue.set(layer.box[1], 1, layer.box[0][1]);
                                                            Vue.set(layer.box[1], 3, layer.box[0][3]);
                                                        }
                                                        if (+pointIndex === 1) {
                                                            Vue.set(layer.box[0], 1, layer.box[1][1]);
                                                            Vue.set(layer.box[0], 3, layer.box[1][3]);
                                                        }
                                                        if (+pointIndex === 2) {
                                                            Vue.set(layer.box[3], 1, layer.box[2][1]);
                                                            Vue.set(layer.box[3], 3, layer.box[2][3]);
                                                        }
                                                        if (+pointIndex === 3) {
                                                            Vue.set(layer.box[2], 1, layer.box[3][1]);
                                                            Vue.set(layer.box[2], 3, layer.box[3][3]);
                                                        }
                                                    }

                                                    if (event.altKey) {
                                                        if (+pointIndex === 0) {
                                                            Vue.set(layer.box[3], 0, layer.box[0][0]);
                                                            Vue.set(layer.box[3], 2, layer.box[0][2]);
                                                        }
                                                        if (+pointIndex === 1) {
                                                            Vue.set(layer.box[2], 0, layer.box[1][0]);
                                                            Vue.set(layer.box[2], 2, layer.box[1][2]);
                                                        }
                                                        if (+pointIndex === 2) {
                                                            Vue.set(layer.box[1], 0, layer.box[2][0]);
                                                            Vue.set(layer.box[1], 2, layer.box[2][2]);
                                                        }
                                                        if (+pointIndex === 3) {
                                                            Vue.set(layer.box[0], 0, layer.box[3][0]);
                                                            Vue.set(layer.box[0], 2, layer.box[3][2]);
                                                        }
                                                    }

                                                    this.updateTransform(layer);
                                                }
                                            }
                                        },
                                    },
                                });
                            });

                            this.$refs.background.forEach((c) => {
                                this.interractables.push(c);

                                interact(c).draggable({
                                    listeners: {
                                        start: (event) => {
                                            const layerIndex = event.target.dataset.layer;

                                            const layer = this.layers[layerIndex];
                                            if (layer) {
                                                const size = this.layerDimensions(layer);

                                                this.centerX = (size.minX + size.maxX) * 0.5;
                                                this.centerY = (size.minY + size.maxY) * 0.5;

                                                const containerLocation = this.$refs.image.getBoundingClientRect();

                                                const x = event.pageX - containerLocation.left;
                                                const y = event.pageY - containerLocation.top;

                                                this.startRadius = Math.sqrt(
                                                    (x - this.centerX) * (x - this.centerX)
                                                        + (y - this.centerY) * (y - this.centerY)
                                                );
                                            }
                                        },

                                        end: (event) => {
                                            const layerIndex = event.target.dataset.layer;

                                            const layer = this.layers[layerIndex];
                                            if (layer) {
                                                if (event.altKey) {
                                                    const containerLocation = this.$refs.image.getBoundingClientRect();

                                                    const x = event.pageX - containerLocation.left;
                                                    const y = event.pageY - containerLocation.top;

                                                    const radius = Math.sqrt(
                                                        (x - this.centerX) * (x - this.centerX)
                                                            + (y - this.centerY) * (y - this.centerY)
                                                    );

                                                    layer.box.forEach((point) => {
                                                        const dx = point[0] - this.centerX;
                                                        const dy = point[1] - this.centerY;

                                                        const d = Math.sqrt(dx * dx + dy * dy);

                                                        const dNow = d * this.startRadius / radius;

                                                        const dxNow = dx / d * dNow;
                                                        const dyNow = dy / d * dNow;

                                                        const xNow = dxNow + this.centerX;
                                                        const yNow = dyNow + this.centerY;

                                                        Vue.set(point, 0, xNow);
                                                        Vue.set(point, 1, yNow);
                                                        Vue.set(point, 2, 0);
                                                        Vue.set(point, 3, 0);
                                                    });

                                                    this.updateTransform(layer);
                                                } else {
                                                    const dx = event.pageX - event.x0;
                                                    const dy = event.pageY - event.y0;

                                                    const width = this.$refs.image.offsetWidth;
                                                    const height = this.$refs.image.offsetHeight;

                                                    layer.box.forEach((point) => {
                                                        Vue.set(point, 0, point[0] + (dx / width * this.w));
                                                        Vue.set(point, 1, point[1] + (dy / height * this.h));
                                                        Vue.set(point, 2, 0);
                                                        Vue.set(point, 3, 0);
                                                    });

                                                    this.updateTransform(layer);
                                                }
                                            }
                                        },

                                        move: (event) => {
                                            const layerIndex = event.target.dataset.layer;

                                            const layer = this.layers[layerIndex];
                                            if (layer) {
                                                if (event.altKey) {
                                                    const containerLocation = this.$refs.image.getBoundingClientRect();

                                                    const x = event.pageX - containerLocation.left;
                                                    const y = event.pageY - containerLocation.top;

                                                    const radius = Math.sqrt(
                                                        (x - this.centerX) * (x - this.centerX)
                                                            + (y - this.centerY) * (y - this.centerY)
                                                    );

                                                    layer.box.forEach((point) => {
                                                        const dx = point[0] - this.centerX;
                                                        const dy = point[1] - this.centerY;

                                                        const d = Math.sqrt(dx * dx + dy * dy);

                                                        const dNow = d * this.startRadius / radius;

                                                        const dxNow = dx / d * dNow;
                                                        const dyNow = dy / d * dNow;

                                                        const xNow = dxNow + this.centerX;
                                                        const yNow = dyNow + this.centerY;

                                                        Vue.set(point, 2, xNow - point[0]);
                                                        Vue.set(point, 3, yNow - point[1]);
                                                    });

                                                    this.updateTransform(layer);
                                                } else {
                                                    const dx = event.pageX - event.x0;
                                                    const dy = event.pageY - event.y0;

                                                    const width = this.$refs.image.offsetWidth;
                                                    const height = this.$refs.image.offsetHeight;

                                                    layer.box.forEach((point) => {
                                                        Vue.set(point, 2, dx / width * this.w);
                                                        Vue.set(point, 3, dy / height * this.h);
                                                    });

                                                    this.updateTransform(layer);
                                                }
                                            }
                                        },
                                    },
                                });
                            });
                        }
                    });
                });
            },

            updateTransform(layer) {
                const matrix = this.computeTransform(
                    layer.boxMapFrom.map(p => [p[0], p[1]]),
                    layer.box.map(p => [p[0], p[1]]),
                );

                layer.matrix3d = this.formatMatrix(matrix);
            },

            computeTransform(src, dst) {
                const count = 4;

                const a = []; // (2*count) x 8 matrix
                const b = []; // (2*count) vector

                for (let i = 0; i < 2 * count; i += 1) {
                    a.push([0, 0, 0, 0, 0, 0, 0, 0]);
                    b.push(0);
                }

                for (let i = 0; i < count; i += 1) {
                    const j = i + count;

                    // eslint-disable-next-line
                    a[i][0] = a[j][3] = src[i][0];
                    // eslint-disable-next-line
                    a[i][1] = a[j][4] = src[i][1];
                    // eslint-disable-next-line
                    a[i][2] = a[j][5] = 1;
                    // eslint-disable-next-line
                    a[i][3] = a[i][4] = a[i][5] = a[j][0] = a[j][1] = a[j][2] = 0;
                    a[i][6] = -src[i][0] * dst[i][0];
                    a[i][7] = -src[i][1] * dst[i][0];
                    a[j][6] = -src[i][0] * dst[i][1];
                    a[j][7] = -src[i][1] * dst[i][1];
                    // eslint-disable-next-line
                    b[i] = dst[i][0];
                    // eslint-disable-next-line
                    b[j] = dst[i][1];
                }

                const x = numeric.solve(a, b);

                const m = [
                    x[0], x[3], 0, x[6],
                    x[1], x[4], 0, x[7],
                    0, 0, 1, 0,
                    x[2], x[5], 0, 1,
                ];

                return m;
            },

            reverseTransform(layer) {
                if (layer.matrix3d && layer.matrix3d.length > 0) {
                    const m = layer.matrix3d.split(',').map(x => +x.trim());

                    const v = layer.boxMapFrom.map(p => [
                        m[0] * p[0] + m[4] * p[1] + m[12],
                        m[1] * p[0] + m[5] * p[1] + m[13],
                        0,
                        m[3] * p[0] + m[7] * p[1] + m[15],
                    ]);

                    v.forEach((p, index) => {
                        layer.box[index][0] = p[0] / p[3];
                        layer.box[index][1] = p[1] / p[3];
                    });
                }
            },

            formatMatrix(m) {
                let transform = '';

                for (let i = 0; i < m.length - 1; i += 1) {
                    transform += m[i].toFixed(6).replace(/0+$/, '') + ',';
                }

                transform += m[15].toFixed(6).replace(/0+$/, '');

                return transform;
            },

            updateScale(deltaScale, dx, dy) {
                this.scale += deltaScale;

                if (this.scale < 0.25) {
                    this.scale = 0.25;
                }

                this.scaleX += dx;
                this.scaleY += dy;

                if (this.scale <= 1) {
                    this.scaleX = 0;
                    this.scaleY = 0;
                }
            },

            matrixUpdated(layer) {
                this.$nextTick(() => {
                    this.reverseTransform(layer);
                });
            },

            roundBoxes(layer) {
                layer.box.forEach((p) => {
                    p[0] = Math.round(p[0]);
                    p[1] = Math.round(p[1]);
                });

                this.updateBoxes(layer);
            },

            rotate180(layer) {
                layer.box.forEach((p) => {
                    const x = Math.cos(Math.PI) * p[0] - Math.sin(Math.PI) * p[1];
                    const y = Math.sin(Math.PI) * p[0] + Math.cos(Math.PI) * p[1];

                    p[0] = x;
                    p[1] = y;
                });

                this.updateBoxes(layer);
            },

            parseUrl() {
                const parts = parse(this.url);

                const account = /image\/([^/]+)/.exec(parts.pathname);

                if (account && account.length > 1) {
                    // eslint-disable-next-line
                    this.account = account[1];
                }

                if (parts.query) {
                    const q = parts.query.substr(1).split('&');

                    const layers = [];

                    let layer = {};
                    let skip = false;

                    q.forEach((p) => {
                        const [name, value] = p.split('=');

                        if (name === 'layer') {
                            layer = this.newLayer();

                            layers.push(layer);

                            skip = false;
                        }

                        if (name === 'effect') {
                            skip = true;
                        }

                        if (skip) {
                            return;
                        }

                        if (name === 'src') {
                            layer.src = value;
                        }

                        if (name === 'omask') {
                            layer.omask = value;
                        }

                        if (name === 'osize') {
                            layer.osize = value;
                        }

                        if (name === 'opos') {
                            layer.opos = value;
                        }

                        if (name === 'oscale') {
                            layer.oscale = value;
                        }

                        if (name === 'orotate') {
                            layer.orotate = value;
                        }

                        if (name === 'mask') {
                            layer.vmask = value;
                        }

                        if (name === 'blend') {
                            layer.blend = value;
                        }

                        if (name === 'matrix3d') {
                            layer.match3d = true;

                            this.updateLayerBox(layer);

                            layer.tempMatrix = value;
                        }
                    });

                    this.layers = layers;
                }
            },

            openUrl() {
                window.open(this.url);
            },
        },
    };
</script>

<style lang="less">
    .builder {
        position: relative;
        display: flex;

        padding-top: 34px;

        width: calc(100% - 40px);
        margin: 20px;

        border: 1px solid #d3dbe3;
        border-radius: 2px;

        label {
            font-weight: normal;
        }
    }

    .builder__input {
        position: absolute;

        left: 0;
        top: 0;
        width: 100%;

        border-bottom: 1px solid #d3dbe3;

        .form-group {
            margin: 0;
        }

        input {
            border: none;
            border-radius: 0;
        }

        button {
            border: none;
            border-radius: 0;
        }

        .input-group-btn .btn.btn-default {
            border: none;
            border-left: 1px solid #d3dbe3;
        }
    }

    .builder__configurator {
        flex: 0 0 auto;

        padding: 20px;

        width: 600px;

        border-right: 1px solid #d3dbe3;

        .box-actions {
            padding: 10px 0;
        }

        input:invalid {
            border: 1px solid #f00;
        }
    }

    .builder__preview {
        position: relative;

        flex: 1 1 auto;

        max-width: 750px;

        padding: 20px;

        &.builder__preview--no-max {
            max-width: none;
        }
    }

    .builder__preview-sticky {
        position: sticky;
        top: 20px;

        word-break: break-all;
    }

    .preview__image-container {
        position: relative;

        width: 100%;
    }

    .preview__image-zoom {
        position: absolute;

        left: 0;
        top: 0;

        width: 100%;
        height: 100%;
    }

    .preview__image-square {
        position: relative;

        width: 100%;
        height: 0;

        padding-top: 100%;

        overflow: hidden;

        background-image: url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAMAAAC6V+0/AAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFn\
ZVJlYWR5ccllPAAAAyhpVFh0WE1MOmNvbS5hZG9iZS54bXAAAAAAADw/eHBhY2tldCBiZWdpbj0i77u/IiBpZD0iVzVNME1wQ2VoaUh6cmVTek5UY3prYzlkIj8+IDx4Onhtc\
G1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IkFkb2JlIFhNUCBDb3JlIDUuNi1jMTQ1IDc5LjE2MzQ5OSwgMjAxOC8wOC8xMy0xNjo0MDoyMiAgICAgIC\
AgIj4gPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4gPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiI\
geG1sbnM6eG1wPSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvIiB4bWxuczp4bXBNTT0iaHR0cDovL25zLmFkb2JlLmNvbS94YXAvMS4wL21tLyIgeG1sbnM6c3RSZWY9\
Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC9zVHlwZS9SZXNvdXJjZVJlZiMiIHhtcDpDcmVhdG9yVG9vbD0iQWRvYmUgUGhvdG9zaG9wIENDIDIwMTkgKE1hY2ludG9za\
CkiIHhtcE1NOkluc3RhbmNlSUQ9InhtcC5paWQ6REU2QjdBREY3MjkxMTFFOTg4RkZEODUwMEQzQTQ1MTYiIHhtcE1NOkRvY3VtZW50SUQ9InhtcC5kaWQ6REU2QjdBRTA3Mj\
kxMTFFOTg4RkZEODUwMEQzQTQ1MTYiPiA8eG1wTU06RGVyaXZlZEZyb20gc3RSZWY6aW5zdGFuY2VJRD0ieG1wLmlpZDpERTZCN0FERDcyOTExMUU5ODhGRkQ4NTAwRDNBNDU\
xNiIgc3RSZWY6ZG9jdW1lbnRJRD0ieG1wLmRpZDpERTZCN0FERTcyOTExMUU5ODhGRkQ4NTAwRDNBNDUxNiIvPiA8L3JkZjpEZXNjcmlwdGlvbj4gPC9yZGY6UkRGPiA8L3g6\
eG1wbWV0YT4gPD94cGFja2V0IGVuZD0iciI/Pizgrq4AAAAGUExURf///+vr63on/egAAAAYSURBVHjaYmCEAwYEGNKCw8QbCEGAAAMApbQAyc5sPE8AAAAASUVORK5CYII=");
    }

    .preview__image {
        position: absolute;

        left: 0;
        top: 0;
        width: 100%;
        height: 100%;

        background-color: transparent;
        background-position: center;
        background-repeat: no-repeat;
        background-size: contain;

        border: 1px solid #f0f0f0;
    }

    .control-box__background {
        position: absolute;
    }

    .control-box__point {
        position: absolute;

        border: 1px solid #333;
        background: #fff;

        width: 11px;
        height: 11px;

        margin-left: -5px;
        margin-top: -5px;
    }

    .preview__controls {
        padding-top: 20px;
        padding-bottom: 20px;
    }
</style>
